// components/Homes.jsx
import { useState, useEffect } from 'react';
import {
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Typography,
  IconButton,
  Box,
  Stack,
  Button,
  Card,
  Grid,
  CircularProgress
} from '@mui/material';
import { ChevronLeft, ChevronRight, Edit, Add, Delete } from '@mui/icons-material';
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Footer from "examples/Footer";
import MLoading from "components/MLoading";

import HomeFormModal from 'components/HomeFormModal';
import { getHomes, createHome, updateHome, deleteHome } from 'api/getApi';

function Homes() {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
  });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingHome, setEditingHome] = useState(null);
  const [mode, setMode] = useState('add');

  const columns = [
    { id: 'name', label: 'Name', width: '30%' },
    { id: 'sort', label: 'Sort', width: '15%' },
    { id: 'title', label: 'Title', width: '30%' },
    { id: 'status', label: 'Status', width: '15%' },
    { id: 'action', label: 'Action', width: '25%' },
  ];

  const getDashboard = async (page = 1) => {
    try {
      setLoading(true);
      const response = await getHomes(page);
      setData(response.data.data || response.data);
      setPagination({
        current_page: response.data.current_page || 1,
        last_page: response.data.last_page || 1,
        per_page: response.data.per_page || 10,
        total: response.data.total || response.data.length
      });
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDashboard(pagination.current_page);
  }, [pagination.current_page]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.last_page) {
      setPagination(prev => ({...prev, current_page: newPage}));
    }
  };

  const handleOpenModal = (home = null) => {
    if (home) {
      setEditingHome(home);
      setMode('edit');
    } else {
      setEditingHome(null);
      setMode('add');
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingHome(null);
  };

  const handleSubmit = async (formData) => {
    try {
      setActionLoading(true);
      if (mode === 'add') {
        await createHome(formData);
      } else {
        await updateHome(editingHome.id, formData);
      }
      getDashboard(pagination.current_page);
      handleCloseModal();
    } catch (error) {
      console.error('Error submitting form:', error);
      setActionLoading(false); 
    } finally {
      setActionLoading(false); 
    }
  };
 
  const handleDelete = async (id) => {
    if (window.confirm('آیا از حذف این بخش مطمئن هستید؟')) {
      try {
        setDeleteLoadingId(id); 
        await deleteHome(id);
        getDashboard(pagination.current_page);
      } catch (error) {
        console.error('Error deleting section:', error);
        setDeleteLoadingId(null);
      } finally {
        setDeleteLoadingId(null);
      }
    }
  };

  if (loading && !data.length) return <MLoading />;

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        <Grid  spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  لیست بخش‌های صفحه اصلی
                </MDTypography>
                <Button
                  variant="contained"
                  color="white"
                  startIcon={<Add />}
                  onClick={() => handleOpenModal()}
                >
                  افزودن بخش جدید
                </Button>
              </MDBox>
              <MDBox pt={3}>
                <Box sx={{ width: '100%', overflow: 'hidden' }}>
                  <TableContainer sx={{ width: '100%' }}>
                    <Table sx={{ width: '100%' }}>
                      <TableHead>
                        <TableRow sx={{ display: 'flex', width: '100%' }}>
                          {columns.map((column) => (
                            <TableCell
                              key={column.id}
                              sx={{ 
                                width: column.width,
                                bgcolor: 'background.default',
                                borderBottom: '2px solid',
                                borderColor: 'divider'
                              }}
                            >
                              <Typography variant="subtitle1" fontWeight={600}>
                                {column.label}
                              </Typography>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {data && data.length > 0 ? (
                          data.map((row) => (
                            <TableRow key={row.id} sx={{ display: 'flex', width: '100%' }}>
                              <TableCell sx={{ width: '30%' }}>
                                <Typography variant="body2">{row.section_name}</Typography>
                              </TableCell>
                              <TableCell sx={{ width: '15%' }}>
                                <Typography variant="body2">{row.sort_order}</Typography>
                              </TableCell>
                              <TableCell sx={{ width: '30%' }}>
                                <Typography variant="body2" noWrap>
                                  {row.title || '---'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ width: '15%' }}>
                                <Box
                                  sx={{
                                    display: 'inline-block',
                                    padding: '4px 12px',
                                    borderRadius: '12px',
                                    backgroundColor: row.is_active ? '#e8f5e8' : '#ffeaea',
                                    color: row.is_active ? '#2e7d32' : '#d32f2f',
                                    fontSize: '0.75rem',
                                    fontWeight: '600'
                                  }}
                                >
                                  {row.is_active ? 'فعال' : 'غیرفعال'}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ width: '25%' }}>
                                <Stack direction="row" spacing={1}>
                                  <IconButton 
                                    onClick={() => handleOpenModal(row)}
                                    color="primary"
                                    size="small"
                                    disabled={actionLoading || deleteLoadingId}
                                  >
                                    <Edit />
                                  </IconButton>
                                  <IconButton 
                                    onClick={() => handleDelete(row.id)}
                                    color="error"
                                    size="small"
                                    disabled={actionLoading || deleteLoadingId === row.id}
                                  >
                                    {deleteLoadingId === row.id ? (
                                      <CircularProgress size={20} color="error" /> 
                                    ) : (
                                      <Delete />
                                    )}
                                  </IconButton>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                هیچ داده‌ای موجود نیست
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination */}
                  {data && data.length > 0 && (
                    <Box display="flex" justifyContent="center" alignItems="center" mt={2} p={2}>
                      <IconButton 
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                        disabled={pagination.current_page === 1}
                        size="small"
                      >
                        <ChevronLeft />
                      </IconButton>
                      <Typography variant="body2" mx={2}>
                        صفحه {pagination.current_page} از {pagination.last_page} 
                        (مجموع: {pagination.total} مورد)
                      </Typography>
                      <IconButton 
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                        disabled={pagination.current_page === pagination.last_page}
                        size="small"
                      >
                        <ChevronRight />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <HomeFormModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        initialData={editingHome}
        mode={mode}
        loading={actionLoading}
      />
    </DashboardLayout>
  );
}

export default Homes;